import type { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/cn';

/**
 * Mount once near the root. Sharing a provider is what makes the second
 * tooltip in a row open instantly instead of waiting out the delay again.
 */
export function TooltipProvider({
  children,
  delayDuration = 300,
}: {
  children: ReactNode;
  delayDuration?: number;
}): JSX.Element {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export interface TooltipProps {
  content: ReactNode;
  /** Single element that accepts a ref — it becomes the trigger via `asChild`. */
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Force open, for a walkthrough. Leave undefined for normal hover/focus behaviour. */
  open?: boolean;
  className?: string;
}

/**
 * A tooltip is a HINT, never the only place information lives: it does not
 * appear on touch at all. Anything a user must know to dose correctly belongs
 * in visible text — which is also why <IconButton> takes a required `label`
 * rather than relying on one of these.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  open,
  className,
}: TooltipProps): JSX.Element {
  return (
    <TooltipPrimitive.Root open={open}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 max-w-64 rounded-md border border-line bg-raised px-2.5 py-1.5',
            'text-xs leading-5 text-ink shadow-pop',
            'data-[state=delayed-open]:animate-[medtrack-fade-in_150ms_ease-out]',
            'data-[state=closed]:animate-[medtrack-fade-out_150ms_ease-out]',
            className,
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-raised" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
